import {
  ProjectDetailsFragment,
  ShareInfoFragment,
  StorageItemFragment,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { ErrorHandler } from '@sketch/tracing'
import { useCallback } from 'react'

import { useTransferStorageItem } from '../../operations'
import {
  TreeNodeTypes,
  useTreeState,
} from '../../../projects/modals/ProjectPickerContent'
import { ConfirmDocumentTransferModal } from './ConfirmTransferModal'
import { useConfirmationModal } from './useConfirmationModal'
import { getLocations } from './getLocation'

export interface MoveStorageItemProps {
  data: TreeNodeTypes
}

export const useMoveStorageItem = (
  item: Pick<StorageItemFragment, '__typename' | 'identifier' | 'name'> &
    (
      | Pick<ShareInfoFragment, '__typename' | 'memberships'>
      | Pick<ProjectDetailsFragment, '__typename'>
    ),
  hideModal: () => void
) => {
  const [askForConfirmation, ConfirmationModal] = useConfirmationModal()
  const { showToast } = useToast()
  const treeState = useTreeState()

  const [transferItem, { loading }] = useTransferStorageItem({
    onError: message => showToast(message, 'negative'),
  })

  const moveStorageItem = useCallback(
    (target: TreeNodeTypes) => {
      const { targetWorkspace, transferLocation } = getLocations(
        target,
        treeState
      )

      if (!transferLocation || !targetWorkspace) {
        ErrorHandler.shouldNeverHappen('target location is not defined')
        return
      }

      const isWithinTheSameWorkspace =
        transferLocation.source.workspace.identifier ===
        transferLocation.destination.workspace.identifier

      if (!isWithinTheSameWorkspace) {
        const targetProject = target.__typename === 'Project' ? target : null

        if (item.__typename === 'Share') {
          askForConfirmation(
            ConfirmDocumentTransferModal,
            {
              targetWorkspace,
              share: item,
              project: targetProject || undefined,
            },
            ({ membershipsOperation }) => {
              // TODO: Pass membershipsOperation once we will support transferring shares using transferStorageItem mutation
              transferItem({
                item,
                location: transferLocation,
                onCompleted: () => hideModal(),
              })
            }
          )
          return
        }
      }

      transferItem({
        item,
        location: transferLocation,
        onCompleted: () => hideModal(),
      })
    },
    [askForConfirmation, hideModal, item, transferItem, treeState]
  )

  return [moveStorageItem, { loading, ConfirmationModal }] as const
}
